import Axios from 'axios'
import { DialogManifestJson } from '../../API/DialogJson'
import { validateDialog } from '../../API/validation'
import Config from '../../Containers/Config'
import DialogContainer from '../../Containers/DialogContainer/DialogContainer'
import SubtitlesContainer from '../../Containers/SubtitlesContainer/SubtitlesContainer'
import AudioController from '../../Controllers/AudioController'
import LoadingController from '../../Controllers/LoadingController'
import Video from '../Video/Video'
import DialogOption, { DialogOptionClickHandler } from './DialogOption'
import DialogPart from './DialogPart'

type DialogProps = {
    id: string
    firstPartId: string
    soundtrackAudioId?: string
    introVideoId?: string
    videos: Array<Video>
    loops: Array<Video>
    randomFailureResponses: Array<Video>
    parts: Array<DialogPart>
}

export default class Dialog {
    element = document.createElement('div')
    id: string
    firstPartId: string
    soundtrackAudioId?: string
    videos: Array<Video>
    loops: Array<Video>
    randomFailureResponses: Array<Video>
    randomsPlayed: Array<number> = []
    parts: Record<string, DialogPart> = {}
    introVideo?: Video
    // TODO
    private handleDialogEnd?: () => void
    currentPart?: DialogPart

    constructor(props: DialogProps) {
        this.element.classList.add('dialog')

        this.id = props.id
        this.firstPartId = props.firstPartId
        this.soundtrackAudioId = props.soundtrackAudioId

        this.videos = props.videos
        this.loops = props.loops
        this.randomFailureResponses = props.randomFailureResponses

        // Add all video elements
        ;[...this.videos, ...this.loops, ...this.randomFailureResponses].forEach((video) => {
            video.hide()
            this.element.appendChild(video.videoElement)
        })

        // add skip on click to response videos
        this.videos.forEach((video) => video.addClickHandler(video.skip))

        this.parts = props.parts.reduce((acc, part) => {
            this.element.appendChild(part.element)
            // WHY: part.loopVideoId is a string that references an index of dialog.loops
            part.setLoop(this.loops[parseInt(part.loopVideoId)])

            Object.keys(part.options).forEach((optionId) => {
                const option = part.options[optionId]
                option.setClickHandler(this.createDialogOptionClickHandler())
            })

            return {
                ...acc,
                [part.id]: part,
            }
        }, {})

        const introVideoIndex = parseInt(props.introVideoId ?? '')
        this.introVideo = !isNaN(introVideoIndex) ? this.videos[introVideoIndex] : undefined

        DialogContainer.addDialog(this)
    }

    static loadDialogManifest = async (dialogId: string) => {
        const baseUrl = `${Config.DIALOG_PATH}${dialogId}/`
        const getAudioUrl = (src: string) => `${baseUrl}audio/${src}${Config.AUDIO_FILE_EXT}`
        const getVideoUrl = (src: string) => `${baseUrl}video/${src}${Config.VIDEO_FILE_EXT}`

        const response = await Axios.get(`${baseUrl}${dialogId}.json`)

        const manifest: DialogManifestJson = response.data

        if (!validateDialog) {
            throw new Error('Inamove::Dialog => could not get validation!')
        }

        if (!validateDialog(manifest)) {
            console.error(
                `Inamove::InventoryCombinationsController => Manifest validation for "${dialogId}" failed:`,
                validateDialog.errors
            )
            throw new Error()
        }

        const withCorrectUrls: DialogManifestJson = {
            ...manifest,
            soundtrack: manifest.soundtrack && getAudioUrl(manifest.soundtrack),
            videos: manifest.videos.map(({ srcURL }) => ({ srcURL: getVideoUrl(srcURL) })),
            loops: manifest.loops.map(({ srcURL }) => ({ srcURL: getVideoUrl(srcURL) })),
            randomFailures: manifest.randomFailures.map(({ srcURL }) => ({ srcURL: getVideoUrl(srcURL) })),
            parts: manifest.parts.map((part) => ({
                ...part,
                options: part.options.map((option) => ({
                    ...option,
                    audio: option.audio && getAudioUrl(option.audio),
                })),
            })),
        }

        return withCorrectUrls
    }

    static load = async (dialogId: string) => {
        const manifest = await Dialog.loadDialogManifest(dialogId)
        console.log(`##### Inamove::Dialog => load: Loaded Dialog manifest for "${dialogId}"`)

        const videos = manifest.videos.map(({ srcURL }) => {
            const video = new Video(srcURL)
            LoadingController.addToQueue(video)
            return video
        })

        const loops = manifest.loops.map(({ srcURL }) => {
            const loop = new Video(srcURL, { loop: true, muted: true })
            LoadingController.addToQueue(loop)
            return loop
        })

        const randomFailureResponses = manifest.randomFailures.map(({ srcURL }) => {
            const video = new Video(srcURL)
            LoadingController.addToQueue(video)
            return video
        })

        const parts = manifest.parts.map((dialogPartManifest) => new DialogPart(dialogPartManifest))

        const props: DialogProps = {
            id: manifest.id,
            firstPartId: manifest.firstPart,
            soundtrackAudioId: manifest.soundtrack && AudioController.add(manifest.soundtrack),
            introVideoId: manifest.intro,
            videos,
            loops,
            randomFailureResponses,
            parts,
        }

        return new Dialog(props)
    }

    getRandomFailureResponse = () => {
        // WHY: When all random responses played, start over again
        if (this.randomsPlayed.length === this.randomFailureResponses.length) {
            this.randomsPlayed = []
        }

        const randomFailureResponseIds = this.randomFailureResponses.map((_, index) => index)
        const availableResponseIds = randomFailureResponseIds.filter((id) => !this.randomsPlayed.includes(id))

        const randomId = Math.round(Math.random() * (availableResponseIds.length - 1))
        this.randomsPlayed.push(availableResponseIds[randomId])

        return this.randomFailureResponses[availableResponseIds[randomId]]
    }

    createDialogOptionClickHandler = (): DialogOptionClickHandler => {
        return async (option: DialogOption) => {
            // hide part options
            option.part.hideOptions()

            const responseId: number | undefined = parseInt(option.responseId)
            const responseVideo =
                responseId !== undefined
                    ? responseId < 0
                        ? this.getRandomFailureResponse()
                        : this.videos[responseId]
                    : undefined

            if (option.audioId) {
                SubtitlesContainer.setText(option.subtitles)
                await AudioController.play(option.audioId)
                SubtitlesContainer.clear()
            }

            if (responseVideo) {
                responseVideo.show()
                await responseVideo.play()
            }

            // WHY: "-1" or "-5" signals dialog exit
            if (parseInt(option.nextDialogPartId) < 0) {
                this.stop()
            } else {
                this.stopCurrentPart()
                this.startPart(option.nextDialogPartId)
            }
        }
    }

    show = () => {
        this.element.classList.remove('hidden')
    }

    hide = () => {
        this.element.classList.add('hidden')
    }

    startPart = (partId: string) => {
        this.currentPart = this.parts[partId]
        this.currentPart.start()
    }

    stopCurrentPart = () => {
        if (this.currentPart !== undefined) {
            this.currentPart.stop()
            this.currentPart = undefined
        }
    }

    start = async () => {
        this.soundtrackAudioId && AudioController.loop(this.soundtrackAudioId)
        DialogContainer.show()
        this.show()

        if (this.introVideo) {
            this.introVideo.show()
            await this.introVideo.play()
            this.introVideo.hide()
        }

        this.startPart(this.firstPartId)

        // save the resolve function for this Promise so we can call it later
        // WHY: so we can use `await dialog.start()`
        return new Promise<void>((resolve) => {
            this.handleDialogEnd = resolve
        })
    }

    stop = () => {
        AudioController.stop(this.soundtrackAudioId)
        DialogContainer.hide()
        this.hide()
        this.stopCurrentPart()

        this.handleDialogEnd && this.handleDialogEnd()
    }

    destroy = () => {
        ;[...this.videos, ...this.loops, ...this.randomFailureResponses].forEach((video) => video.destroy())
        this.videos = []
        this.loops = []
        this.randomFailureResponses = []
        this.introVideo?.destroy()
        this.soundtrackAudioId && AudioController.remove(this.soundtrackAudioId)
        Object.keys(this.parts).forEach((partId) => this.parts[partId].destroy())
        this.element.remove()
    }
}
